import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Typography } from '@material-ui/core';
import Layout from '../../common/Layout';
import UserHeader from '../../common/components/UserHeader/UserHeader';
import RankingBlock from '../../common/components/RankingBlock/RankingBlock';
import LoadingSpinner from "../../common/components/LoadingSpinner/LoadingSpinner";
import { useRankingStats } from "../../hooks/useRankingStats";
import { useRankingChallenge } from "../../hooks/useRankingChallenge";
import { useRankingCareer } from "../../hooks/useRankingCareer";
import { useRankingSelect } from "../../hooks/useRankingSelect";
import useAppStore from "../../store/appStore";


const useStyles = makeStyles(theme => ({
  section: {
    marginBottom:50
  },
  sectionTitle: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    marginBottom:20,
  },
}));

const GridWithXXL = ({ xxl, ...other }) => { // Used to add extra breakpoint to Grid
  const xxlClass = `MuiGrid-grid-xxl-${xxl}`;
  return <Grid className={xxlClass} {...other} />;
};

const Dashboard = props => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const userData = useAppStore(state => state.user);

  const {
    loading: cdaLoading,
    error: cdaError,
    data: cdaData
  } = useRankingSelect();

  const [selectedChallengeValue, setSelectedChallengeValue] = useState('');
  const [selectedCareerValue, setSelectedCareerValue] = useState('');

  // Inizializza i valori di default quando i dati sono disponibili
  useEffect(() => {
    if (cdaData?.challengeDefinitionsAssigned?.[0]?.id && !selectedChallengeValue) {
      setSelectedChallengeValue(cdaData.challengeDefinitionsAssigned[0].id);
    }
  }, [cdaData, selectedChallengeValue]);

  useEffect(() => {
    if (cdaData?.careerPathsNames?.[0]?.slug && !selectedCareerValue) {
      setSelectedCareerValue(cdaData.careerPathsNames[0].slug);
    }
  }, [cdaData, selectedCareerValue]);

  const challengeValue = selectedChallengeValue;
  const careerValue = selectedCareerValue;



  const {
    isFetching: rcLoading,
    error: rcError,
    data: rcData,
  } = useRankingChallenge(challengeValue);

  const {
    isFetching: rgLoading,
    error: rgError,
    data: rgData,
  } = useRankingCareer(careerValue);

    const { isFetching: usLoading, error: usError, data: usData } = useRankingStats();

  // const globalContent = gcData?.globalContent?.data?.attributes;
  const userStatistics = usData?.myStatistics;
  const rankingOverall = usData?.rankingOverall;
  const rankingChallenge = rcData?.rankingChallenge;
  const rankingGroup = rgData?.rankingGroup;

  const isTrial = userData ? userData.userType === 'b2c-trial' : null;
  //const enablePrice = userData ? (userData.userType === 'b2c-trial' || userData.userType === 'b2b-expired') : null;

  const menuListChallenge = cdaData && cdaData.challengeDefinitionsAssigned.map(item => {
    return {
      value: item.id,
      title: item.challengeTitle
    }
  })

  const menuListCareer = cdaData && cdaData.careerPathsNames.map(item => {
    return {
      value: item.slug,
      title: item.name
    }
  })

  const changeChallenge = (event) => {
    setSelectedChallengeValue(event.target.value);
  };

  const changeGroup = (event) => {
    setSelectedCareerValue(event.target.value);
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  }

  /*const handleDialogClose = () => {
    setDialogOpen(false);
  };*/

  return (
    <Layout>
      { usLoading ? (
        <LoadingSpinner boxed={true} styles={{ paddingTop: 30 }} />
      ) :  usError ? (
        <div>Error! {usError?.message || 'Unknown error'}</div>
      ) : (
      <>
        {/*{isTrial ?
          <SubscriptionDialog
            dialogClose={handleDialogClose}
            dialogOpen={dialogOpen}
            enablePrice={enablePrice}
            title={globalContent.subscriptionDialog.title}
            subTitle={globalContent.subscriptionDialog.description}
            pricingPlan={globalContent.pricingPlan}
            ctaFaqs={globalContent.subscriptionDialog.ctaFaqs}
          />
        : null*/}
        {userStatistics ? (
          <UserHeader
            name={userData.me.name ?? ''}
            photo={userData.me.photo ?? ''}
            overall={userStatistics.totalScore}
            totalChallenges={userStatistics.totalFinishedChallenges}
            totalLearningTopics={userStatistics.totalFinishedLearningTopics}
            totalCareers={userStatistics.totalFinishedCareers}
            badgeEarned={userStatistics.gainedBadges}
          />
        ) : (
          <LoadingSpinner boxed={true} styles={{ paddingTop: 15, paddingBottom: 15 }} />
        )}
      </>
      )}

      <section className={classes.section}>
        <Typography className={classes.sectionTitle} variant="h3">
          Rankings
        </Typography>
        <Grid container spacing={3}>

          {usLoading ? (
            <GridWithXXL item xs={12} lg={6} xxl={4}>
              <LoadingSpinner boxed={true} styles={{ paddingTop: 30 }} />
            </GridWithXXL>
          ) : usError ? (
            <GridWithXXL item xs={12} lg={6} xxl={4}>
              <div>Error! {usError?.message || 'Unknown error'}</div>
            </GridWithXXL>
          ) : rankingOverall ? (
            <GridWithXXL item xs={12} lg={6} xxl={4}>
              <RankingBlock
                title="Overall Ranking"
                menu={false}
                dataTable={rankingOverall}
                openDialog={handleDialogOpen}
                isTrial={isTrial}
              />
            </GridWithXXL>
          ) : null}

          {rcLoading || cdaLoading ? (
            <GridWithXXL item xs={12} lg={6} xxl={4}>
              <LoadingSpinner boxed={true} styles={{ paddingTop: 30 }} />
            </GridWithXXL>
          ) : rcError || cdaError ? (
            <GridWithXXL item xs={12} lg={6} xxl={4}>
              <div>Error! {rcError?.message || ''} {cdaError?.message || ''}</div>
            </GridWithXXL>
          ) : rankingChallenge && menuListChallenge ? (
            <GridWithXXL item xs={12} lg={6} xxl={4}>
              <RankingBlock
                title="Challenge Ranking"
                menu={true}
                dataMenu={menuListChallenge}
                dataTable={rankingChallenge}
                onSelectChange={changeChallenge}
                selectedValue={selectedChallengeValue}
                openDialog={handleDialogOpen}
                isTrial={isTrial}
              />
            </GridWithXXL>
          ) : null}

          {rgLoading ? (
            <GridWithXXL item xs={12} lg={6} xxl={4}>
              <LoadingSpinner boxed={true} styles={{ paddingTop: 30 }} />
            </GridWithXXL>
          ) : rgError ? (
            <GridWithXXL item xs={12} lg={6} xxl={4}>
              <div>Error! {rgError?.message || 'Unknown error'}</div>
            </GridWithXXL>
          ) : rankingGroup && menuListCareer ? (
            <GridWithXXL item xs={12} lg={6} xxl={4}>
              <RankingBlock
                title="Career Ranking"
                menu={true}
                dataMenu={menuListCareer}
                dataTable={rankingGroup}
                onSelectChange={changeGroup}
                selectedValue={selectedCareerValue}
                openDialog={handleDialogOpen}
                isTrial={isTrial}
              />
            </GridWithXXL>
          ) : null}

        </Grid>
      </section>
    </Layout>
  )

}

export default Dashboard;
